import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs/Subscription';
import { Broker, BrokerSubscriber, Platform } from '@c4/core';
import { ToolbarService, ToolbarContext, C4ToolbarAction } from '@c4/ui';
import { ConnectedDeviceService, VersionsService } from '../services';

// subclasses must implement this method that gets called when broker is connected
export interface IOnBrokerConnectedInit {
  onBrokerConnectedInit(): void;
}

export abstract class SysmanComponentBase implements IOnBrokerConnectedInit {

  toolbarActions: Array<C4ToolbarAction>;
  connDevice: Observable<any>; // current connected device
  subscriptions: Subscription[] = [];
  brokerHost: string = "";
  onBrokerInit: Function = null; // subclass method to call when broker is connected
  isUpdateAvailable: boolean = false;
  uptime: number;

//  isWorking: Observable<boolean>;
  isReady: Observable<boolean>;
  // subclasses must implement this method that gets called when broker is connected
  abstract onBrokerConnectedInit(): void;

  constructor(private _router: Router, private _route: ActivatedRoute, private _store: Store<any>,
    private _broker: Broker, private _platform: Platform,
    private _connectedDeviceService: ConnectedDeviceService,
    private _versionService: VersionsService) {

    this.connDevice = this._store.select(s => s.connectedDevice);
    //this.isWorking = this._store.select(s => (s.connectedDevice.working));
    this.isReady = this._store.select(s => (!s.connectedDevice.working));
    
    // get connected device and working state
    this.subscriptions.push(
      this._store.select(s => s.connectedDevice).subscribe(
        device => {
          if (device !== undefined) {
            if (device.host !== undefined)
              this.brokerHost = device.host;
          }
        })
    );

    this.toolbarActions = [
      {
        label: 'Network Details',
        iconClass: 'fa fa-fw fa-list',
        name: 'details'
      },
      {
        label: 'Discovery',
        iconClass: 'fa fa-fw fa-eye',
        name: 'disco'
      },
      {
        label: 'Logs',
        iconClass: 'fa fa-fw fa-database',
        name: 'logs'
      },
      {
        label: 'Services',
        iconClass: 'fa fa-fw fa-cogs',
        name: 'services'
      },
      {
        label: 'VPN Setup',
        iconClass: 'fa fa-fw fa-code-fork',
        name: 'setup'
      },
      {
        label: 'Time',
        iconClass: 'fa fa-fw fa-clock-o',
        name: 'time'
      },
      /*     {
            label: 'SDDP Trace',
            iconClass: 'fa fa-fw fa-hourglass-o',
            name: 'sddptrace'
          }, */
      {
        label: 'Packages',
        iconClass: 'fa fa-fw fa-gift',
        name: 'packages'
      },
      {
        label: 'Output Log',
        iconClass: 'fa fa-fw fa-newspaper-o',
        name: 'outputlog'
      }
    ];
  }

  // must be called called by subclass' ngOnInit
  OnInit() {
    // only do this once if broker isn't connected yet
    if (!this._connectedDeviceService.isConnected) {
      this._broker.connected
        .filter(connected => connected)
        .take(1)
        .subscribe(connected => {
          if (connected == true) {
            // once broker connects, get info about connected host
            this._platform.getPlatformStatus().then(
              (result: any) => {
                console.log('in getPlatformStatus()', result);
                this._connectedDeviceService.setWorking(false);
                this._connectedDeviceService.setHost(result.directorIP);

                let directorVersion = this.getDirectorVersion(result);
                this._connectedDeviceService.setConnection({ uuid: result.directorUUID, address: result.directorIP, directorName: result.directorName, version: directorVersion });

                // check for updates available
                if (result.controllerDriverExists) {
                  this._versionService.getUpateVersions().then(
                    (updates: any) => {
                      this.isUpdateAvailable = updates.primaryControllerUpdateAvailable;
                    },
                    (err: any) => { }
                  );
                }

                // if a callback is defined call the subclass method
                if (this.onBrokerInit !== null)
                  this.onBrokerInit();
              },
              (err: any) => {
                this._connectedDeviceService.setWorking(false);
                console.log('in sysman-base - getPlatformStatus() error', err);

                // director is disabled - mini-broker is probably running
                if (err.status == 404) {
                  // get broker host
                  try {
                      let host = err.url.split("/")[2];
                      this._connectedDeviceService.setHost(host);
                  }
                  catch (exc) {}

                  // if a callback is defined call the subclass method
                  if (this.onBrokerInit !== null)
                    this.onBrokerInit();
                }
              });
          }
        });
    }
    else { // broker is already connected, call the subclass method
      if (this.onBrokerInit !== null)
        this.onBrokerInit();
    }
  }

  // method to call when broker is initalized
  setBrokerInit(_onBrokerInit: Function) {
    this.onBrokerInit = _onBrokerInit;
  }

  destroy() {
    console.log("In base - destroy()");
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // gets the director version from a platform_status broker result
  getDirectorVersion(platformStatus: any): string {

    var directorVersion = "";
    if (platformStatus.versions && (platformStatus.versions.length > 0)) {
      var director = platformStatus.versions.filter(item => {
        return (item.name == "Director");
      });
      if (director.length > 0) {
        directorVersion = director[0].version;
      }
    }
    return directorVersion;
  }

  // toolbar callbacks
  toolbarActionInvoked(action: any) {

    switch (action.action) {
      case 'disco':
        this._router.navigate(['../../sysman/disco'], { relativeTo: this._route });
        break;
      case 'details':
        this._router.navigate(['../../sysman/details'], { relativeTo: this._route });
        break;
      case 'logs':
        this._router.navigate(['../../sysman/logging'], { relativeTo: this._route });
        break;
      case 'services':
        this._router.navigate(['../../sysman/services'], { relativeTo: this._route });
        break;
      case 'setup':
        this._router.navigate(['../../sysman/setup'], { relativeTo: this._route });
        break;
      case 'time':
        this._router.navigate(['../../sysman/time'], { relativeTo: this._route });
        break;
      /*      case 'sddptrace':
              this._router.navigate(['../../sysman/sddptrace'],{relativeTo: this._route});
              break; */
      case 'outputlog':
        this._router.navigate(['../../sysman/outputlog'], { relativeTo: this._route });
        break;
      case 'packages':
        this._router.navigate(['../../sysman/packages'], { relativeTo: this._route });
        break;
      default:
        break;
    }
  }
}

@Component({
  selector: 'sys-sysman',
  templateUrl: './sysman.component.html',
  providers: []
})
export class SysmanComponent {

  constructor() { }

}



// WEBPACK FOOTER //
// ./~/@c4/sysman/src/app/sysman/sysman.component.ts